/**
 * Global Error Handler
 *
 * Sets up global error handling for unhandled errors and promise rejections
 */

interface ErrorReport {
  type: 'error' | 'unhandledRejection';
  message: string;
  stack?: string;
  timestamp: string;
  url?: string;
  userAgent?: string;
}

class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private errorQueue: ErrorReport[] = [];
  private isInitialized = false;

  private constructor() {}

  static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  initialize() {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    // Handle unhandled errors
    window.addEventListener('error', (event) => {
      this.handleError({
        type: 'error',
        message: event.message,
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        url: event.filename
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError({
        type: 'unhandledRejection',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        timestamp: new Date().toISOString()
      });
    });

    this.isInitialized = true;
  }

  private handleError(errorReport: ErrorReport) {
    // Add user agent if available
    if (typeof navigator !== 'undefined') {
      errorReport.userAgent = navigator.userAgent;
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Global Error Handler:', errorReport);
    }

    // Add to error queue
    this.errorQueue.push(errorReport);

    // Keep only last 50 errors to prevent memory issues
    if (this.errorQueue.length > 50) {
      this.errorQueue = this.errorQueue.slice(-50);
    }

    // TODO: Send to error tracking service
    // this.sendToErrorTrackingService(errorReport);
  }

  // Get recent errors for debugging
  getRecentErrors(): ErrorReport[] {
    return [...this.errorQueue];
  }

  // Clear error queue
  clearErrors() {
    this.errorQueue = [];
  }

  // Method to manually report an error
  reportError(error: Error, context?: Record<string, unknown>) {
    this.handleError({
      type: 'error',
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      ...context
    });
  }

  // Placeholder for error tracking service integration
  private sendToErrorTrackingService(_errorReport: ErrorReport) {
    // TODO: Implement error tracking service integration
    // Example:
    // if (window.Sentry) {
    //   window.Sentry.captureException(new Error(errorReport.message), {
    //     extra: errorReport,
    //   });
    // }
  }
}

// Export singleton instance
export const globalErrorHandler = GlobalErrorHandler.getInstance();

// Initialize on import
if (typeof window !== 'undefined') {
  globalErrorHandler.initialize();
}
